<template>
  <v-container :fluid="selectedTable !== tableTypes.ENROLLMENT">
    <v-row v-if="selectedTable === tableTypes.ENROLLMENT" class="pb-5">
      <v-spacer />
      <enrollment-toggle :pre-selected-table="selectedSubTable" @change="onSelectSubTable" />
    </v-row>
    <v-data-table
      class="sponsor-table show-hover"
      :headers="headers"
      :items="formattedSummaries"
      :options="options"
      :loading="loading"
      :items-per-page.sync="options.itemsPerPage"
      :expanded="formattedSummaries"
      :footer-props="{
        itemsPerPageOptions: [5, 10, 25]
      }"
      data-e2e-id="trial-summary-table"
      @current-items="currentItems"
      @update:options="onUpdateOptions"
    >
      <template v-for="(h, index) in headers" v-slot:[`header.${h.value}`]>
        <span :key="`${h.value}-${index}`">{{ h.text }} </span>
        <app-tooltip
          v-if="getHeaderTooltip(selectedTable, h.value).length > 0"
          :key="h.value"
          color="dark"
          right
          max-width="250px"
        >
          <template #activator="{ on }">
            <v-icon small v-on="on">info</v-icon>
          </template>
          <div
            v-for="(descriptionLine, descriptionIndex) in getHeaderTooltip(selectedTable, h.value)"
            :key="descriptionIndex"
          >
            <span>{{ descriptionLine }}</span>
            <br />
          </div>
        </app-tooltip>
      </template>
      <template #item="{ item }">
        <insights-table-row
          :item="item"
          :selected-trial-id="selectedTrialId"
          :prescreening-class="getClass(tableTypes.PRE_SCREENING)"
          :summary-class="getClass(tableTypes.SUMMARY)"
          :enrollment-class="getClass(tableTypes.ENROLLMENT)"
          :distribution-class="getClass(tableTypes.DISTRIBUTION)"
          :enrollment-monthly-class="
            getClass(tableTypes.ENROLLMENT, subTableTypes.enrollment.MONTHLY)
          "
          :enrollment-total-class="getClass(tableTypes.ENROLLMENT, subTableTypes.enrollment.TOTAL)"
          :enrollment-projected-class="
            getClass(tableTypes.ENROLLMENT, subTableTypes.enrollment.PROJECTED)
          "
          :potential-care-team-members="potentialCareTeamMembers"
          @select-trial="selectTrial"
          @on-edit-trial="onEditSiteTrial"
        />
      </template>
      <template #expanded-item="{ item }">
        <template v-for="summary in item.children">
          <insights-table-row
            :key="summary.id"
            :item="summary"
            nested
            :selected-trial-id="selectedTrialId"
            :prescreening-class="getClass(tableTypes.PRE_SCREENING)"
            :summary-class="getClass(tableTypes.SUMMARY)"
            :enrollment-class="getClass(tableTypes.ENROLLMENT)"
            :distribution-class="getClass(tableTypes.DISTRIBUTION)"
            :enrollment-monthly-class="
              getClass(tableTypes.ENROLLMENT, subTableTypes.enrollment.MONTHLY)
            "
            :enrollment-total-class="
              getClass(tableTypes.ENROLLMENT, subTableTypes.enrollment.TOTAL)
            "
            :enrollment-projected-class="
              getClass(tableTypes.ENROLLMENT, subTableTypes.enrollment.PROJECTED)
            "
            :potential-care-team-members="potentialCareTeamMembers"
            :trial-contracted="item.trialContracted"
            @select-trial="selectTrial"
            @on-edit-trial="onEditSiteTrial"
          />
        </template>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import _ from 'lodash'
import { DateFormatFilter } from '@/filters/date-format'
import moment from 'moment'
import { durationMixin } from '@/mixins/duration'
import uuid from 'uuid'
import {
  ENROLLMENT_PRECISION,
  getHeaderTooltip,
  SubTableTypes,
  TableTypes
} from '@/helpers/table-helper'
import EnrollmentToggle from '@/components/insights/EnrollmentToggle'
import { fullName } from '@/helpers/name-helper'
import { membershipService } from '@/services/membership-service'
import { MyActiveOrganization } from '@/helpers/my-account-helper'
import InsightsTableRow from '@/components/insights/InsightsTableRow'

export default {
  components: {
    InsightsTableRow,
    EnrollmentToggle
  },
  mixins: [durationMixin],
  props: {
    generateSummaryDisplayName: { type: Function },
    loading: { type: Boolean, default: true },
    options: { type: Object, default: () => {} },
    selectedTable: { type: String, default: TableTypes.SUMMARY },
    selectedTrialId: { type: String, default: null },
    summaries: { type: Array, default: () => [] },
    selectFirstRow: { type: Boolean, default: true }
  },
  data() {
    return {
      selectedSubTable: SubTableTypes.enrollment.TOTAL,
      potentialCareTeamMembers: null
    }
  },
  computed: {
    orgId() {
      return MyActiveOrganization.id
    },
    subTableTypes() {
      return SubTableTypes
    },
    tableTypes() {
      return TableTypes
    },
    formattedSummaries() {
      return _.map(this.summaries, summary => {
        return _.merge({}, summary, {
          key: uuid(),
          displayName: this.generateSummaryDisplayName(summary),
          children: _.map(summary.children, nested => {
            return _.merge({}, nested, {
              key: uuid(),
              displayName: this.generateSummaryDisplayName(nested)
            })
          })
        })
      })
    },
    headers() {
      const star = {
        sortable: false,
        value: 'favorite',
        width: '32px'
      }
      const trial = {
        text: 'Trial',
        sortable: true,
        value: 'trialId'
      }
      const edit = {
        sortable: false,
        width: '32px'
      }
      const preScreening = [
        {
          text: 'Average Pre-Screening',
          sortable: true,
          value: 'averageDaysPreScreening',
          align: 'end',
          class: this.getClass(TableTypes.PRE_SCREENING)
        },
        {
          text: 'Average Awaiting Review',
          sortable: true,
          value: 'averageDaysAwaitingReview',
          align: 'end',
          class: this.getClass(TableTypes.PRE_SCREENING)
        },
        {
          text: 'Average In Review',
          sortable: true,
          value: 'averageDaysInReview',
          align: 'end',
          class: this.getClass(TableTypes.PRE_SCREENING)
        },
        {
          text: 'Total Identified',
          sortable: true,
          value: 'patientCount',
          align: 'end',
          class: this.getClass(TableTypes.PRE_SCREENING)
        },
        {
          text: 'Awaiting Review',
          sortable: true,
          value: 'patientAwaitingReviewCount',
          align: 'end',
          class: this.getClass(TableTypes.PRE_SCREENING)
        },
        {
          text: 'In Review',
          sortable: true,
          value: 'patientInReviewCount',
          align: 'end',
          class: this.getClass(TableTypes.PRE_SCREENING)
        },
        {
          text: 'Pre-Screened',
          sortable: true,
          value: 'patientCompletedCount',
          align: 'end',
          class: this.getClass(TableTypes.PRE_SCREENING)
        }
      ]
      const summary = [
        {
          text: 'Time Elapsed',
          sortable: true,
          value: 'currentDuration',
          class: this.getClass(TableTypes.SUMMARY)
        },
        {
          text: 'Performance',
          sortable: true,
          value: 'monthlyEnrollmentDifference',
          class: this.getClass(TableTypes.SUMMARY),
          align: 'end'
        },
        {
          text: 'Pre-Screening Average',
          sortable: true,
          value: 'averageDaysPreScreening',
          class: this.getClass(TableTypes.SUMMARY),
          align: 'end'
        },
        {
          text: 'Pre-Screened',
          sortable: true,
          value: 'patientCompletedCount',
          class: this.getClass(TableTypes.SUMMARY),
          align: 'end'
        },
        {
          text: 'Following',
          sortable: true,
          value: 'patientFollowingCount',
          class: this.getClass(TableTypes.SUMMARY),
          align: 'end'
        },
        {
          text: 'Matched',
          sortable: true,
          value: 'patientMatchedCount',
          class: this.getClass(TableTypes.SUMMARY),
          align: 'end'
        },
        {
          text: 'Enrolled',
          sortable: true,
          value: 'patientEnrolledCount',
          class: this.getClass(TableTypes.SUMMARY),
          align: 'end'
        },
        {
          text: 'Not Consented',
          sortable: true,
          value: 'patientNotConsentedCount',
          class: this.getClass(TableTypes.SUMMARY),
          align: 'end'
        },
        {
          text: 'Not Eligible',
          sortable: true,
          value: 'patientNotEligibleCount',
          class: this.getClass(TableTypes.SUMMARY),
          align: 'end'
        }
      ]
      const enrollment = [
        {
          text: 'Time Elapsed',
          sortable: true,
          value: 'currentDuration',
          class: this.getClass(TableTypes.ENROLLMENT)
        },
        {
          text: 'Target Total Duration',
          sortable: true,
          value: 'targetDuration',
          class: this.getClass(TableTypes.ENROLLMENT),
          width: '185px'
        },
        {
          text: 'Expected Monthly Enrollment Rate',
          sortable: true,
          value: 'targetMonthlyEnrollment',
          class: this.getClass(TableTypes.ENROLLMENT, SubTableTypes.enrollment.MONTHLY),
          align: 'end'
        },
        {
          text: 'Actual Monthly Enrollment Rate',
          sortable: true,
          value: 'averageMonthlyEnrollment',
          class: this.getClass(TableTypes.ENROLLMENT, SubTableTypes.enrollment.MONTHLY),
          align: 'end'
        },
        {
          text: 'Expected Enrollment',
          sortable: true,
          value: 'plannedEnrollmentToDate',
          class: this.getClass(TableTypes.ENROLLMENT, SubTableTypes.enrollment.TOTAL),
          align: 'end'
        },
        {
          text: 'Actual Enrollment',
          sortable: true,
          value: 'patientEnrolledCount',
          class: this.getClass(TableTypes.ENROLLMENT, SubTableTypes.enrollment.TOTAL),
          align: 'end'
        },
        {
          text: 'Target Total Enrollment',
          sortable: true,
          value: 'targetEnrollment',
          class: this.getClass(TableTypes.ENROLLMENT, SubTableTypes.enrollment.PROJECTED),
          align: 'end'
        },
        {
          text: 'Projected Total Enrollment',
          sortable: true,
          value: 'projectedTotalEnrollment',
          class: this.getClass(TableTypes.ENROLLMENT, SubTableTypes.enrollment.PROJECTED),
          align: 'end'
        },
        {
          text: 'Percentage Difference',
          sortable: true,
          value: 'monthlyEnrollmentDifference',
          class: this.getClass(TableTypes.ENROLLMENT),
          align: 'end'
        }
      ]
      const distribution = [
        {
          text: 'Total Identified',
          sortable: true,
          value: 'patientCount',
          align: 'end',
          class: this.getClass(TableTypes.DISTRIBUTION)
        },
        {
          text: 'Awaiting Review',
          sortable: true,
          value: 'patientAwaitingReviewCount',
          align: 'end',
          class: this.getClass(TableTypes.DISTRIBUTION)
        },
        {
          text: 'In Review',
          sortable: true,
          value: 'patientInReviewCount',
          align: 'end',
          class: this.getClass(TableTypes.DISTRIBUTION)
        },
        {
          text: 'Following',
          sortable: true,
          value: 'patientFollowingCount',
          align: 'end',
          class: this.getClass(TableTypes.DISTRIBUTION)
        },
        {
          text: 'Matched',
          sortable: true,
          value: 'patientMatchedCount',
          align: 'end',
          class: this.getClass(TableTypes.DISTRIBUTION)
        },
        {
          text: 'Enrolled',
          sortable: true,
          value: 'patientEnrolledCount',
          align: 'end',
          class: this.getClass(TableTypes.DISTRIBUTION)
        },
        {
          text: 'Not Consented',
          sortable: true,
          value: 'patientNotConsentedCount',
          align: 'end',
          class: this.getClass(TableTypes.DISTRIBUTION)
        },
        {
          text: 'Not Eligible',
          sortable: true,
          value: 'patientNotEligibleCount',
          align: 'end',
          class: this.getClass(TableTypes.DISTRIBUTION)
        }
      ]
      let selectedTableHeaders
      if (this.selectedTable === TableTypes.PRE_SCREENING) {
        selectedTableHeaders = preScreening
      } else if (this.selectedTable === TableTypes.SUMMARY) {
        selectedTableHeaders = summary
      } else if (this.selectedTable === TableTypes.ENROLLMENT) {
        selectedTableHeaders = enrollment
      } else {
        selectedTableHeaders = distribution
      }
      return _.concat(star, trial, selectedTableHeaders, edit)
    }
  },
  created() {
    this.fetchPotentialCareTeamMembers()
  },
  methods: {
    async fetchPotentialCareTeamMembers() {
      let { data } = await membershipService.list(this.orgId, {
        'userRole.id': 'TRIALS_COORDINATOR'
      })
      this.potentialCareTeamMembers = _.map(data, membership => {
        return _.merge({}, membership.user, { displayName: fullName(membership.user) })
      })
    },
    fetchTrialSummaries() {
      this.$emit('fetch-trial-summaries')
    },
    selectTrial(trialId) {
      this.$emit('select-trial', trialId)
    },
    formatStartDate(startDate) {
      return DateFormatFilter(startDate)
    },
    formatCompletionDate(completionDate) {
      return moment(completionDate).format('MMM, YYYY')
    },
    async onEditSiteTrial(site) {
      this.$emit('edit-site-trial', site)
      await this.fetchTrialSummaries()
    },
    getClass(tableType, subTableTypes) {
      return tableType === this.selectedTable &&
        (_.isNil(subTableTypes) || _.includes(_.concat([], subTableTypes), this.selectedSubTable))
        ? ''
        : 'd-none'
    },
    formatNumber(number, digits = 0) {
      return _.isNil(number) ? number : number.toFixed(digits)
    },
    formatEnrollment(number) {
      return this.formatNumber(number, ENROLLMENT_PRECISION)
    },
    currentItems(items) {
      if (this.selectFirstRow && !_.isEmpty(items)) {
        this.selectTrial(_.get(_.head(items), 'trialId'))
        this.$emit('select-first-row', false)
      }
    },
    getHeaderTooltip(table, columnValue) {
      return getHeaderTooltip(table, columnValue)
    },
    onUpdateOptions(options) {
      this.$emit('update:options', options)
    },
    onSelectSubTable(subTableType) {
      this.selectedSubTable = subTableType
    },
    favoriteIcon(summary) {
      return summary.favorite ? 'mdi-star' : 'mdi-star-outline'
    },
    favoriteColor(summary) {
      return summary.favorite ? 'favoritedColor' : null
    },
    changeFavorite(summary) {
      this.$emit('change-favorite', summary)
    }
  }
}
</script>

<style lang="scss" scoped>
.v-tooltip__content {
  opacity: 1 !important;
}
</style>
